import React, { useEffect, useMemo } from 'react';
import * as mobxReact from 'mobx-react';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import './emails-template-form-presentation.scss';
import DefaultButton from '../../../../../global/default-button/default-button';
import { getErrorMessage } from '../../../../../../infrastructure/utils/passport.utils';
import { EmailsTemplateFormVM } from './emails-template-form.viewmodel';
import { EmailsTemplateFormHeader } from './emails-template-form-header/emails-template-form-header';
import EmailTemplatesFormBody from './emails-template-form-body/emails-template-form-body';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { UploadImageS3Presentation } from '../../../shared/upload-image/upload-image-to-s3.presentation';
import OptionsButton from '../../../../../global/options-button/options-button';
import classNames from 'classnames';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../global/text/text';
import { EmailTemplateType } from '../../../../../../api/email-configuration.dao';
import { APPLY_TEXT, CANCEL_TEXT, LOGO_IMAGE_TEXT, MORE_BUTTON_TEXT } from '../../../../../../constants/common-labels.constants';
import {
    DISABLED_RESPONSIVE_EMAIL_INFO_TEXT,
    REGISTRATION_EMAIL_IS_BEING_SAVED_TEXT,
    RESPONSIVE_EMAILS_DELETE_TEXT,
    RESPONSIVE_EMAILS_ENABLED_TEXT,
    STARTING_TEMPLATE_TEXT,
    TRIAGE_QUESTION_ANSWER_TEXT,
    REGISTRATION_CALENDAR_INVITATION_TEXT,
    REGISTRATION_CALENDAR_INVITATION_TITLE
} from './emails-template-form.texts';
import { EnablementStatus } from '../../../../../../constants/common.constants';
import { EmailVariablesSection } from '../../../../../global/email-variables/email-variables';
import { EMAIL_LIST_TYPE } from '../../../../../../constants/email-variables.constants';

interface IProps {
    intl: IntlShape;
    vm: EmailsTemplateFormVM;
}

const EmailsTemplateFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { vm, intl } = props;
    const { enabled, name, autoSubscribed, type } = vm.selectedEmailTemplate;

    const emailTemplateOptions = [
        {
            label: intl.formatMessage(RESPONSIVE_EMAILS_DELETE_TEXT),
            disabled: vm.saving,
            action: vm.deleteEmailTemplate,
            danger: true
        }
    ].filter(Boolean);

    useEffect(() => {
        if (typeof vm.uploadEmailLogoImageS3VM.getImageChanged === 'boolean') {
            vm.emailFormChanged(true);
        }
    }, [vm.uploadEmailLogoImageS3VM.getImageChanged]);

    const isApplyDisabled = !vm.isEmailFormChanged || vm.saving || vm.getTemplateEmailErrors[1];
    const errorMessage = useMemo(() => getErrorMessage(
        intl,
        vm.getTemplateEmailErrors[0]
    ),
        [
            intl,
            vm.getTemplateEmailErrors[0].length
        ]
    );
    const isCalendarInvitationRestricted = !!vm.selectedEmailTemplate?.type &&
        (vm.selectedEmailTemplate.type === EmailTemplateType.REGISTRATION_CALENDAR_INVITATION ||
            vm.selectedEmailTemplate.type === EmailTemplateType.MEETING_CALENDAR_INVITATION ||
            vm.selectedEmailTemplate.type === EmailTemplateType.BOOKABLE_MEETING_CALENDAR_INVITATION ||
            vm.selectedEmailTemplate.type === EmailTemplateType.PRESENTER_BOOKABLE_MEETING_CALENDAR_INVITATION);
    const isMeetingCalendarInvitation = type === EmailTemplateType.MEETING_CALENDAR_INVITATION;
    const isBookableMeetingCalendarInvitation = type === EmailTemplateType.BOOKABLE_MEETING_CALENDAR_INVITATION;
    const isPresenterBookableMeetingCalendarInvitation = type === EmailTemplateType.PRESENTER_BOOKABLE_MEETING_CALENDAR_INVITATION;

    return (
        <CommonPopupBodyFooterLayout
            className={errorMessage ? 'emails-template-form-hidden-scroll' : 'hidden-scroll'}
            body={
                <>
                    <EmailsTemplateFormHeader
                        options={vm.emailTemplatesNames}
                        selectedOption={vm.currentTemplate}
                        setSelectedOption={vm.setCurrentTemplate}
                        handleSendPreview={() => vm.handleSendPreview(intl, vm.currentTemplate)}
                        errorMessage={errorMessage}
                        isCreatedEmail={vm.isCustomEmailTemplate}
                    />
                    <div
                        className={classnames('emails-template-form', {
                            'emails-template-form-with-error': errorMessage,
                        })}
                    >
                        <div className='oe-form'>
                            <div className='oe-form__body'>
                                <FormField>
                                    <div className={classNames('website-custom__checkbox-label m-top-15px m-bottom-0px', {
                                        'website-custom__checkbox-label--disabled': !enabled
                                    })}>

                                        {!isMeetingCalendarInvitation
                                            && !isBookableMeetingCalendarInvitation
                                            && !isPresenterBookableMeetingCalendarInvitation
                                            && (
                                            <OEToggle
                                                isSelected={type === EmailTemplateType.REGISTRATION_CALENDAR_INVITATION ? autoSubscribed : enabled}
                                                onClick={() =>
                                                    type === EmailTemplateType.REGISTRATION_CALENDAR_INVITATION
                                                        ? vm.toggleAutoSubscribedEnabled()
                                                        : vm.toggleEmailEnabled()
                                                }
                                            />
                                        )}

                                        {!isCalendarInvitationRestricted ? (
                                            <Text
                                                {...RESPONSIVE_EMAILS_ENABLED_TEXT}
                                                values={{
                                                    suffix: enabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                                }}
                                            />
                                        ) : name === REGISTRATION_CALENDAR_INVITATION_TITLE ? (
                                            <Text
                                                {...REGISTRATION_CALENDAR_INVITATION_TEXT}
                                                values={{
                                                    suffix: autoSubscribed ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                                }}
                                            />
                                        ) : ''}
                                    </div>
                                    {!isCalendarInvitationRestricted && !enabled && (
                                        <div className='website-custom__checkbox-label-hint'>
                                            <Text {...DISABLED_RESPONSIVE_EMAIL_INFO_TEXT} />
                                        </div>
                                    )}
                                </FormField>
                                {vm.isCustomEmailTemplate &&
                                    <div className='emails-template-form__select-wrapper'>
                                        <FormField
                                            label={intl.formatMessage(STARTING_TEMPLATE_TEXT)}
                                        >
                                            <input
                                                className='oe-form-field__control'
                                                readOnly
                                                disabled
                                                value={
                                                    vm.emailTemplates.find(t => t.type === vm.selectedEmailTemplate.type)?.name
                                                }
                                            />
                                        </FormField>
                                        <FormField
                                            label={intl.formatMessage(TRIAGE_QUESTION_ANSWER_TEXT)}
                                        >
                                            <input
                                                className='oe-form-field__control'
                                                readOnly
                                                disabled
                                                value={vm.getSelectedTemplateSubtype}
                                            />
                                        </FormField>
                                    </div>}
                                {!isCalendarInvitationRestricted && <UploadImageS3Presentation
                                    vm={vm.uploadEmailLogoImageS3VM}
                                    fieldName={intl.formatMessage(LOGO_IMAGE_TEXT)}
                                />}
                                <EmailVariablesSection type={EMAIL_LIST_TYPE.RESPONSIVE}/>
                                <EmailTemplatesFormBody vm={vm} />
                            </div>
                        </div>
                    </div>
                </>
            }
            footer={
                <>
                    {vm.saving &&
                        <span className='oe-popup-layout__footer-status-area'>
                            {intl.formatMessage(REGISTRATION_EMAIL_IS_BEING_SAVED_TEXT)}
                        </span>
                    }

                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultButton
                            text={intl.formatMessage(APPLY_TEXT)}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => vm.handleSubmitEmailForm(intl)}
                            disabled={isApplyDisabled}
                        />
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(CANCEL_TEXT)}
                        />
                        {vm.isCustomEmailTemplate &&
                            <OptionsButton
                                className='options-button--right-margin-auto'
                                modifier={['fat', 'options']}
                                label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                                options={emailTemplateOptions}
                                position='top'
                                align='start'
                            />
                        }
                    </div>
                </>
            }
        />
    );
}));

export { EmailsTemplateFormPresentation };
